import {
  createDefaultUnmanagedBusinessAutoRegisterOptionalRegisterItem,
  createDefaultUnmanagedBusinessAutoRegisterSettingConditionItem,
  getConditionSearchTypesByFieldType,
  unmanagedBusinessAutoRegisterConditionFieldTypeLabels,
  unmanagedBusinessAutoRegisterConditionFieldTypeValues,
  unmanagedBusinessAutoRegisterConditionSearchTypeLabels,
  unmanagedBusinessAutoRegisterConditionSearchTypeValues,
  unmanagedBusinessAutoRegisterFieldLabels,
  unmanagedBusinessAutoRegisterFieldValues,
  unmanagedBusinessAutoRegisterRegisterUnitLabels,
  unmanagedBusinessAutoRegisterRegisterUnitValues,
  UnmanagedBusinessAutoRegisterConditionFieldType,
  UnmanagedBusinessAutoRegisterConditionSearchType,
  UnmanagedBusinessAutoRegisterField,
  UnmanagedBusinessAutoRegisterRegisterUnit,
  UnmanagedBusinessAutoRegisterSettingConditionItem,
} from '@/coreDomainsModule/constants/unmanagedBusinessAutoRegister';
import { i18n } from '@/i18n';

const t = i18n.t;

/**
 * 自動登録設定で利用するラベル/選択肢の解決ロジックを提供する
 */
export const useUnmanagedBusinessAutoRegisterLabels = () => {
  /**
   * 条件項目に対応する表示ラベルを取得する
   * @param targetFieldType - 条件項目
   */
  const getConditionFieldTypeLabel = (targetFieldType: UnmanagedBusinessAutoRegisterConditionFieldType): string => {
    return t(unmanagedBusinessAutoRegisterConditionFieldTypeLabels[targetFieldType]) as string;
  };

  /**
   * 検索方法に対応する表示ラベルを取得する
   * @param searchType - 検索方法
   */
  const getConditionSearchTypeLabel = (searchType: UnmanagedBusinessAutoRegisterConditionSearchType): string => {
    return t(unmanagedBusinessAutoRegisterConditionSearchTypeLabels[searchType]) as string;
  };

  /**
   * 項目値に対応する表示ラベルを取得する
   * @param targetField - 項目値
   */
  const getFieldLabel = (targetField: UnmanagedBusinessAutoRegisterField): string => {
    const fieldLabel = unmanagedBusinessAutoRegisterFieldLabels[targetField];
    if (targetField === 'moq') {
      return fieldLabel;
    }
    return t(fieldLabel) as string;
  };

  /**
   * 登録項目単位に対応する表示ラベルを取得する
   * @param registerUnit - 登録項目単位
   */
  const getRegisterUnitLabel = (registerUnit: UnmanagedBusinessAutoRegisterRegisterUnit): string => {
    return t(unmanagedBusinessAutoRegisterRegisterUnitLabels[registerUnit]) as string;
  };

  /**
   * 条件項目の選択肢を取得する
   */
  const getConditionFieldTypeOptions = (): string[] => {
    return unmanagedBusinessAutoRegisterConditionFieldTypeValues.map((targetFieldType) => getConditionFieldTypeLabel(targetFieldType));
  };

  /**
   * 任意項目の選択肢を取得する
   * @param optionalRegisterFields - 任意項目の一覧
   */
  const getOptionalFieldOptions = (optionalRegisterFields: UnmanagedBusinessAutoRegisterField[]): string[] => {
    return optionalRegisterFields.map((targetField) => getFieldLabel(targetField));
  };

  /**
   * 登録単位の選択肢を取得する
   */
  const getRegisterUnitOptions = (): string[] => {
    return unmanagedBusinessAutoRegisterRegisterUnitValues.map((registerUnit) => getRegisterUnitLabel(registerUnit));
  };

  /**
   * SelectInputの選択値から条件項目値へ変換する
   * @param label - SelectInputの表示ラベル
   */
  const convertLabelToConditionFieldType = (label: string): UnmanagedBusinessAutoRegisterConditionFieldType => {
    const matchedFieldType = unmanagedBusinessAutoRegisterConditionFieldTypeValues.find((targetFieldType) => {
      const rawLabel = unmanagedBusinessAutoRegisterConditionFieldTypeLabels[targetFieldType];
      return label === rawLabel || label === getConditionFieldTypeLabel(targetFieldType);
    });
    return matchedFieldType ?? createDefaultUnmanagedBusinessAutoRegisterSettingConditionItem().targetFieldType;
  };

  /**
   * SelectInputの選択値から検索方法へ変換する
   * @param label - SelectInputの表示ラベル
   */
  const convertLabelToConditionSearchType = (label: string): UnmanagedBusinessAutoRegisterConditionSearchType => {
    const matchedSearchType = unmanagedBusinessAutoRegisterConditionSearchTypeValues.find((searchType) => {
      const rawLabel = unmanagedBusinessAutoRegisterConditionSearchTypeLabels[searchType];
      return label === rawLabel || label === getConditionSearchTypeLabel(searchType);
    });
    return matchedSearchType ?? createDefaultUnmanagedBusinessAutoRegisterSettingConditionItem().searchType;
  };

  /**
   * SelectInputの選択値から対象項目へ変換する
   * @param label - SelectInputの表示ラベル
   */
  const convertLabelToTargetField = (label: string): UnmanagedBusinessAutoRegisterField => {
    const matchedTargetField = unmanagedBusinessAutoRegisterFieldValues.find((targetField) => {
      const rawLabel = unmanagedBusinessAutoRegisterFieldLabels[targetField];
      return label === rawLabel || label === getFieldLabel(targetField);
    });
    return matchedTargetField ?? createDefaultUnmanagedBusinessAutoRegisterOptionalRegisterItem().targetField;
  };

  /**
   * SelectInputの選択値から登録単位へ変換する
   * @param label - SelectInputの表示ラベル
   */
  const convertLabelToRegisterUnit = (label: string): UnmanagedBusinessAutoRegisterRegisterUnit => {
    const matchedRegisterUnit = unmanagedBusinessAutoRegisterRegisterUnitValues.find((registerUnit) => {
      const rawLabel = unmanagedBusinessAutoRegisterRegisterUnitLabels[registerUnit];
      return label === rawLabel || label === getRegisterUnitLabel(registerUnit);
    });
    return matchedRegisterUnit ?? unmanagedBusinessAutoRegisterRegisterUnitValues[0];
  };

  /**
   * 条件項目に対して選択可能な検索方法ラベルを取得する
   * @param targetFieldType - 条件項目
   */
  const getSearchTypeLabelsByFieldType = (targetFieldType: UnmanagedBusinessAutoRegisterConditionFieldType): string[] => {
    return getConditionSearchTypesByFieldType(targetFieldType).map((searchType) => getConditionSearchTypeLabel(searchType));
  };

  /**
   * 条件1件分で使用する検索方法ラベルを取得する
   * @param conditionItem - 条件
   */
  const getSearchTypeLabelsByConditionItem = (conditionItem: UnmanagedBusinessAutoRegisterSettingConditionItem): string[] => {
    return getSearchTypeLabelsByFieldType(conditionItem.targetFieldType);
  };

  /**
   * 条件1件分で使用する検索方法表示値を取得する
   * @param conditionItem - 条件
   */
  const getConditionSearchTypeSelectLabel = (conditionItem: UnmanagedBusinessAutoRegisterSettingConditionItem): string => {
    const availableSearchTypes = getConditionSearchTypesByFieldType(conditionItem.targetFieldType);
    const normalizedSearchType = availableSearchTypes.includes(conditionItem.searchType)
      ? conditionItem.searchType
      : availableSearchTypes[0];
    return getConditionSearchTypeLabel(normalizedSearchType);
  };

  return {
    getConditionFieldTypeLabel,
    getConditionSearchTypeLabel,
    getFieldLabel,
    getRegisterUnitLabel,
    getConditionFieldTypeOptions,
    getOptionalFieldOptions,
    getRegisterUnitOptions,
    convertLabelToConditionFieldType,
    convertLabelToConditionSearchType,
    convertLabelToTargetField,
    convertLabelToRegisterUnit,
    getSearchTypeLabelsByFieldType,
    getSearchTypeLabelsByConditionItem,
    getConditionSearchTypeSelectLabel,
  };
};
